import { useState } from 'react';
import { createFileRoute, redirect, Link } from '@tanstack/react-router';
import { ArrowLeft, MapPin, ShieldAlert, Tag, UserRound, Wrench } from 'lucide-react';
import { LocationActions } from '@/components/organisms/locations/LocationActions';
import { useAuthStore } from '@/stores/auth.store';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/atoms/card';
import { Skeleton } from '@/components/atoms/skeleton';
import { useLocation } from '@/hooks/locations/useLocations';
import { useEquipment } from '@/hooks/equipment/useEquipment';
import { LocationContactsCard } from '@/components/organisms/locations/LocationContactsCard';
import {
  LocationEstimatesPane,
  LocationJobsPane,
} from '@/components/organisms/locations/LocationWorkPanes';
import {
  LocationSectionNav,
  type LocationSectionId,
} from '@/components/organisms/locations/LocationSectionNav';
import { DetailPageLayout } from '@/components/layouts/DetailPageLayout';
import { useJobs } from '@/hooks/jobs/useJobs';
import { useEstimates } from '@/hooks/estimates/useEstimates';
import { formatLocationAddress, locationDisplayName } from '@/types/location';
import { dateStringToDate } from '@/lib/date-values';
import { format } from 'date-fns';

export const Route = createFileRoute('/_dashboard/dashboard/locations/$locationId')({
  beforeLoad: () => {
    const { permissions } = useAuthStore.getState();
    if (permissions.length > 0 && !permissions.includes('contacts:view')) {
      throw redirect({ to: '/dashboard/home' });
    }
  },
  validateSearch: (search: Record<string, unknown>): { from?: 'contact' } => ({
    from: search.from === 'contact' ? 'contact' : undefined,
  }),
  component: LocationDetailPage,
});

/**
 * A certificate expiry is a DATE-ONLY value. Running it through an instant
 * formatter shifts it a day in negative-offset zones, which would render a
 * live certificate as already expired — so parse to local midnight instead.
 *
 * Guarded rather than asserted: `dateStringToDate` returns undefined for a
 * truthy-but-malformed value, and a bare `!` there would throw inside render
 * and blank the ENTIRE detail page over one bad tax row.
 */
function formatExpiry(value: string | null): string {
  if (!value) return '—';
  const parsed = dateStringToDate(value);
  if (!parsed || Number.isNaN(parsed.getTime())) return '—';
  return format(parsed, 'MMM d, yyyy');
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="text-muted-foreground text-xs uppercase tracking-wide">{label}</div>
      <div className="text-sm">{value ?? '—'}</div>
    </div>
  );
}

function LocationDetailPage() {
  const { locationId } = Route.useParams();
  const { from } = Route.useSearch();
  const [section, setSection] = useState<LocationSectionId>('summary');
  const { data: location, isLoading, isError } = useLocation(locationId);

  // Equipment is the one child collection already keyed on a real FK
  // (equipment.serviceAddressId), so it can be shown today. Jobs and estimates
  // still hang off the customer — their tabs arrive with waves 3 and 4, when
  // they gain locationId.
  // Counts for the nav badges. Each is a one-row page read for its total, the
  // same trick the contact page uses, so the badge is right before its pane
  // has ever been opened.
  const { data: jobCount } = useJobs({ locationId, limit: 1 });
  const { data: estimateCount } = useEstimates({ locationId, limit: 1 });
  const { data: equipment } = useEquipment(
    { serviceAddressId: locationId, limit: 25 },
    Boolean(location),
  );

  if (isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (isError || !location) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Location not found.</p>
        <Button asChild variant="link" className="px-0">
          <Link to="/dashboard/locations">Back to locations</Link>
        </Button>
      </div>
    );
  }

  const equipmentRows = equipment?.items ?? [];
  const mapQuery =
    location.lat != null &&
    location.lng != null &&
    Number.isFinite(location.lat) &&
    Number.isFinite(location.lng)
      ? `${location.lat},${location.lng}`
      : [formatLocationAddress(location), location.country].filter(Boolean).join(', ');

  const renderSection = () => {
    switch (section) {
      case 'jobs':
        return (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Jobs at this location</CardTitle>
            </CardHeader>
            <CardContent className="p-0 sm:p-0">
              <LocationJobsPane locationId={location.id} />
            </CardContent>
          </Card>
        );
      case 'estimates':
        return (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Estimates for this location</CardTitle>
            </CardHeader>
            <CardContent className="p-0 sm:p-0">
              <LocationEstimatesPane locationId={location.id} />
            </CardContent>
          </Card>
        );
      case 'taxes':
        return (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Taxes</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <Field label="Jurisdiction" value={location.taxJurisdiction} />
              <Field
                label="Exempt"
                value={location.isTaxExempt ? <Badge variant="secondary">Exempt</Badge> : 'No'}
              />
              {location.isTaxExempt ? (
                <>
                  <Field label="Certificate" value={location.taxExemptCertificate} />
                  <Field label="Expires" value={formatExpiry(location.taxExemptExpiry)} />
                </>
              ) : null}
            </CardContent>
          </Card>
        );
      case 'equipment':
        return (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Equipment at this location</CardTitle>
            </CardHeader>
            <CardContent>
              {equipmentRows.length === 0 ? (
                <p className="text-muted-foreground text-sm">No equipment recorded here yet.</p>
              ) : (
                <ul className="divide-y text-sm">
                  {equipmentRows.map((item) => (
                    <li key={item.id} className="flex items-center justify-between py-2">
                      <span>
                        {item.name}
                        {item.serialNumber ? (
                          <span className="text-muted-foreground ml-2 font-mono text-xs">
                            {item.serialNumber}
                          </span>
                        ) : null}
                      </span>
                      <Badge variant="outline">{item.status}</Badge>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        );
      default:
        return (
          <>
            <Card className="overflow-hidden pb-0">
              <CardHeader className="gap-4">
                <CardTitle className="flex items-center gap-2 text-base">
                  <MapPin className="h-4 w-4" />
                  Location
                </CardTitle>
                <dl className="grid min-w-0 gap-4 sm:grid-cols-2">
                  <div className="min-w-0 space-y-1">
                    <dt className="text-xs font-medium text-muted-foreground">Title</dt>
                    <dd className="break-words text-base font-medium">
                      {location.title?.trim() || (
                        <span className="text-muted-foreground">Not set</span>
                      )}
                    </dd>
                  </div>
                  <div className="min-w-0 space-y-1">
                    <dt className="text-xs font-medium text-muted-foreground">Label</dt>
                    <dd className="break-words text-sm">
                      {location.label?.trim() || (
                        <span className="text-muted-foreground">Not set</span>
                      )}
                    </dd>
                  </div>
                  <div className="min-w-0 space-y-1 border-t pt-4 sm:col-span-2">
                    <dt className="text-xs font-medium text-muted-foreground">Address</dt>
                    <dd className="break-words text-sm">{formatLocationAddress(location)}</dd>
                  </div>
                </dl>
              </CardHeader>
              <CardContent className="p-0 sm:p-0">
                <iframe
                  title={`Map of ${locationDisplayName(location)}`}
                  src={`https://www.google.com/maps?q=${encodeURIComponent(mapQuery)}&z=15&output=embed`}
                  className="block h-64 w-full border-0 border-t"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </CardContent>
            </Card>
            <LocationContactsCard
              locationId={location.id}
              owner={location.customer}
              title="People"
            />

            {location.accessNotes ? (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <ShieldAlert className="h-4 w-4" />
                    Access notes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="whitespace-pre-wrap text-sm">{location.accessNotes}</p>
                </CardContent>
              </Card>
            ) : null}
          </>
        );
    }
  };

  return (
    <DetailPageLayout
      contentClassName="flex min-h-0 gap-0 space-y-0 overflow-hidden p-0"
      header={
        <div className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 max-w-full flex-1 basis-80 items-start gap-3">
            <Button asChild variant="ghost" size="icon" className="mt-5 size-7 shrink-0">
              {from === 'contact' ? (
                <Link
                  to="/dashboard/contacts/$contactId"
                  params={{ contactId: location.customerId }}
                  search={{ tab: 'locations' }}
                  aria-label="Back to contact"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Link>
              ) : (
                <Link to="/dashboard/locations" aria-label="Back to locations">
                  <ArrowLeft className="h-4 w-4" />
                </Link>
              )}
            </Button>
            <div className="min-w-0 flex-1 space-y-1.5">
              <p className="font-mono text-[11px] tracking-wide text-muted-foreground">
                {location.locationNumber}
              </p>
              <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5">
                <h1 className="min-w-0 break-words text-xl font-semibold leading-7 tracking-tight [overflow-wrap:anywhere]">
                  {locationDisplayName(location)}
                </h1>
                {location.label?.trim() ? (
                  <Badge
                    variant="secondary"
                    className="max-w-full gap-1.5 whitespace-normal px-2.5 py-1 font-normal"
                    title={`Label: ${location.label.trim()}`}
                  >
                    <Tag className="shrink-0 text-muted-foreground" aria-hidden="true" />
                    <span className="sr-only">Label: </span>
                    <span className="min-w-0 [overflow-wrap:anywhere]">
                      {location.label.trim()}
                    </span>
                  </Badge>
                ) : null}
              </div>
              <div className="flex min-w-0 flex-col gap-x-4 gap-y-1.5 text-sm text-muted-foreground sm:flex-row sm:flex-wrap">
                <p className="flex min-w-0 items-start gap-1.5">
                  <MapPin className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                  <span className="min-w-0 [overflow-wrap:anywhere]">
                    {formatLocationAddress(location)}
                  </span>
                </p>
                {location.customer ? (
                  <div className="flex min-w-0 items-start gap-1.5">
                    <UserRound className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                    <span className="shrink-0">Customer</span>
                    <Link
                      to="/dashboard/contacts/$contactId"
                      params={{ contactId: location.customerId }}
                      className="min-w-0 rounded-sm font-medium text-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [overflow-wrap:anywhere]"
                    >
                      {location.customer.companyName || location.customer.displayName}
                    </Link>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex flex-wrap gap-1">
              {location.isPrimary ? <Badge>Primary</Badge> : null}
              {location.isService ? (
                <Badge variant="secondary">
                  <Wrench className="mr-1 h-3 w-3" />
                  Service
                </Badge>
              ) : null}
              {location.isBilling ? <Badge variant="outline">Billing</Badge> : null}
              {location.status === 'INACTIVE' ? (
                <Badge variant="destructive">Inactive</Badge>
              ) : null}
              {location.source === 'BACKFILL' ? (
                <Badge
                  variant="outline"
                  title="Created by a migration from a document snapshot — may duplicate another location."
                >
                  Backfilled
                </Badge>
              ) : null}
            </div>
            <LocationActions location={location} />
          </div>
        </div>
      }
    >
      <LocationSectionNav
        active={section}
        onSelect={setSection}
        counts={{
          jobs: jobCount?.total,
          estimates: estimateCount?.total,
          equipment: equipment?.total ?? equipmentRows.length,
        }}
      />
      <div className="min-w-0 flex-1 space-y-4 overflow-y-auto px-6 py-5">{renderSection()}</div>
    </DetailPageLayout>
  );
}
